/**
 * Utility module to provide a persistent registry of tiers added to PCs
 * This ensures that tiers are correctly tracked even across component mounts/unmounts
 */

// Global registry to track all handling tiers that have been added across the app
// Using a Map where keys are pcIds and values are Sets of tier names that have been added
const globalTierRegistry = new Map<string, Set<string>>();

// Registry version to help force re-renders when needed
let registryVersion = 0;

// Custom event name for tier updates
export const TIER_ADDED_EVENT = 'tier-registry-update';

/**
 * Add a tier to the global registry
 * @param pcId Production Capability ID
 * @param tierName Name of the tier being added
 */
export function registerAddedTier(pcId: string, tierName: string): void {
    if (!globalTierRegistry.has(pcId)) {
        globalTierRegistry.set(pcId, new Set<string>());
    }
    
    globalTierRegistry.get(pcId)!.add(tierName);
    
    // Increment version to force re-renders
    registryVersion++;
    
    // Dispatch a custom event to notify all components
    // Using setTimeout to defer event dispatch until after current callstack completes
    setTimeout(() => {
        try {
            const event = new CustomEvent(TIER_ADDED_EVENT, { 
                detail: { pcId, tierName, timestamp: Date.now() } 
            });
            window.dispatchEvent(event);
        } catch (e) {
            // Silent error in production
        }
    }, 0);
}

/**
 * Check if a tier has been added to a specific PC
 * @param pcId Production Capability ID
 * @param tierName Name of the tier to check
 * @returns true if the tier has been added to the PC, false otherwise
 */
export function isTierAdded(pcId: string, tierName: string): boolean {
    if (!globalTierRegistry.has(pcId)) {
        return false;
    }
    
    return globalTierRegistry.get(pcId)!.has(tierName);
}

/**
 * Get all tiers that have been added to a specific PC
 * @param pcId Production Capability ID
 * @returns Array of tier names added to the PC
 */
export function getAddedTiers(pcId: string): string[] {
    if (!globalTierRegistry.has(pcId)) {
        return [];
    }
    
    return Array.from(globalTierRegistry.get(pcId)!);
}

/**
 * Get the current registry version
 * @returns The current registry version number
 */
export function getRegistryVersion(): number {
    return registryVersion;
}

/**
 * Force increment the registry version to trigger re-renders
 */
export function forceRegistryUpdate(): void {
    registryVersion++;
}

/**
 * Debug function to log the entire registry state 
 * (kept for potential debugging needs, but doesn't log in production)
 */
export function logTierRegistry(): void {
    // No-op in production
    return;
}
